import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import {
  Text,
} from '../../common';
import Setting from './Setting';
import {
  SettingsButton,
  SettingsContainer,
  SettingsWrapper,
} from './styles';
import { usePlayer } from '../../../PlayerContext';
import * as playerActions from '../../../actions/player-events';
import {
  setAudioLang,
  setSubtitleLang,
  setSubtitlesEnabled,
} from '../../../../player/utils/request';
import { SUBTITLES_OFF } from '../../../../common/constants';
import platform, { OS_TYPES, IS_SAFARI } from '../../../../player/utils/platform';
import { getLanguage } from '../../../../player/utils/utils';

const noChoice = arr => arr.length < 2;

function Settings({ controlBarVisible, toggleProgressBar, displayProgressBarTimeoutRef, isAudio, isQuality }) {
  const player = usePlayer();

  const {
    audioTracks,
    videoTracks,
    textTracks,
    isPlayingAds,
    audioTrack,
    subtitleTrack,
    bitrate,
  } = useSelector(({ playback }) => ({
    audioTracks: playback.audioTracks,
    videoTracks: playback.videoTracks,
    textTracks: playback.textTracks,
    isPlayingAds: playback.isPlayingAds,
    audioTrack: playback.audioTrack,
    subtitleTrack: playback.subtitleTrack,
    bitrate: playback.bitrate,
  }), shallowEqual);

  const dispatch = useDispatch();
  const settingsContainerRef = useRef();

  const [menuVisible, setMenuVisible] = useState(false);

  // close menu if control bar is hidden
  useEffect(() => {
    if (menuVisible && !controlBarVisible) {
      setMenuVisible(false);
    }
  }, [menuVisible, controlBarVisible]);

  const noOptions = useMemo(() => {
    if (isAudio) return noChoice(audioTracks) && noChoice(textTracks);
    if (isQuality) return noChoice(videoTracks);
    return noChoice(audioTracks) && noChoice(textTracks) && noChoice(videoTracks);
  }, [isAudio, isQuality, audioTracks, textTracks, videoTracks]);
  const settingsDisabled = noOptions || isPlayingAds;
  const isOpen = menuVisible && !settingsDisabled;
  const isMobile = [OS_TYPES.IOS, OS_TYPES.ANDROID].includes(platform.os);

  // Hide quality-only Settings button on Safari since it only has 'auto' option
  if (isQuality && IS_SAFARI) {
    return null;
  }

  const changeAudioTrack = (track) => {
    const languageCode = getLanguage(track);
    dispatch(playerActions.changeAudioTrack(track));
    player.changeAudioTrack(track);

    if (languageCode.length === 2) { // do not save undefined and other unknown values
      setAudioLang(languageCode);
    }
  };

  const changeTextTrack = (track) => {
    dispatch(playerActions.changeTextTrack(track));
    player.changeTextTrack(track);
    if (track === SUBTITLES_OFF) {
      setSubtitlesEnabled(false);
    } else {
      setSubtitleLang(track);
    }
  };

  const changeBitrate = (track) => {
    dispatch(playerActions.changeBitrate(track));
    player.changeBitrate(track);
  };

  const hoveredStateTimeoutRef = useRef(null);
  const onMouseEnter = useCallback(() => {
    clearTimeout(displayProgressBarTimeoutRef.current);
    clearTimeout(hoveredStateTimeoutRef.current);
    setMenuVisible(true);
    toggleProgressBar(false);
  }, []);
  const onMouseLeave = useCallback(() => {
    displayProgressBarTimeoutRef.current = setTimeout(() => {
      toggleProgressBar(true);
    }, 200);
    hoveredStateTimeoutRef.current = setTimeout(() => {
      setMenuVisible(false);
    }, 200);
  }, []);

  const onClick = useCallback(() => {
    if (!isOpen) {
      onMouseEnter();
    }
  }, [isOpen, onMouseEnter]);

  useEffect(() => {
    if (!isMobile || !isOpen) return () => {};
    const handleClickOutside = (e) => {
      if (settingsContainerRef.current && !settingsContainerRef.current.contains(e.target)) {
        onMouseLeave();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isOpen]);

  return (
    <SettingsWrapper>
      <SettingsContainer
        ref={settingsContainerRef}
        {...!isMobile && {
          onMouseEnter,
          onMouseLeave,
        }}
        isOpen={isOpen}
      >
        {isAudio ? (
          <>
            <Setting
              action={changeAudioTrack}
              title={<Text id="playerSettings.audio" />}
              options={audioTracks}
              selectedOption={audioTrack}
            />
            <Setting
              action={changeTextTrack}
              title={<Text id="playerSettings.subtitles" />}
              options={textTracks}
              selectedOption={subtitleTrack}
            />
          </>
        ) : null}
        {isQuality ? (
          <Setting
            action={changeBitrate}
            title={<Text id="playerSettings.quality" />}
            options={[...videoTracks].reverse()}
            selectedOption={bitrate}
          />
        ) : null}
      </SettingsContainer>
      <SettingsButton
        {...isMobile
          ? { onClick }
          : { onMouseEnter, onMouseLeave }}
        disabled={settingsDisabled}
        isAudio={isAudio}
        isQuality={isQuality}
      />
    </SettingsWrapper>
  );
}
Settings.propTypes = {
  controlBarVisible: PropTypes.bool.isRequired,
  toggleProgressBar: PropTypes.func.isRequired,
  displayProgressBarTimeoutRef: PropTypes.shape({ current: PropTypes.number }).isRequired,
  isAudio: PropTypes.bool,
  isQuality: PropTypes.bool,
};
export default React.memo(Settings);
